import * as MarketplaceAPI from "api/MarketplaceAPI";
import * as BlueprintAPI from "api/BlueprintAPI";

import { toast } from "react-toastify";

import { isEnabled } from "utils/featureFlags";

import * as blueprintReducer from "containers/Blueprint/reducer";
import * as loginReducer from "containers/Login/reducer";

import { updateIsBlueprintCreating } from "containers/App/reducer";
import { getUserID, getUserName } from "containers/Login/reducer";
import { getIsDefaultAccount } from "containers/Accounts/reducer";

const RESET_MARKETPLACE = "marketplace/RESET_MARKETPLACE";
const UPDATE_FIELD = "marketplace/UPDATE_FIELD";
const UPDATE_MARKETPLACE = "marketplace/UPDATE_MARKETPLACE";
const UPDATE_OFFER = "marketplace/UPDATE_OFFER";
const DELETE_OFFER = "marketplace/DELETE_OFFER";
const UPDATE_BLUEPRINT = "marketplace/UPDATE_BLUEPRINT";
const DELETE_BLUEPRINT = "marketplace/DELETE_BLUEPRINT";

const INTERNAL_MARKETPLACE_DESCRIPTION = "A private marketplace to share blueprints with others in your workspace";

const MARKETPLACE_FLAGS = {
  PUBLISHED_EXTERNAL: "PUBLISHED_EXTERNAL",
  PUBLISHED_INTERNAL: "PUBLISHED_INTERNAL",
  UNPUBLISHED: "UNPUBLISHED"
};

const OFFER_STATUS = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  ARCHIVED: "ARCHIVED"
};

export const OFFER_TYPE = {
  BLUEPRINTS: "BLUEPRINT"
};

const initialState = {
  blueprints: {},
  isLoading: false,
  selected: null
};

function marketplaceReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case UPDATE_MARKETPLACE:
      return {
        ...state,
        [action.marketplaceId]: {
          ...state[action.marketplaceId],
          [action.key]: action.data
        }
      };
    case UPDATE_OFFER:
      return {
        ...state,
        [action.marketplaceId]: {
          ...state[action.marketplaceId],
          offers: {
            ...state[action.marketplaceId].offers,
            [action.key]: action.data
          }
        }
      };
    case DELETE_OFFER:
      const offers = { ...state[action.marketplaceId].offers };
      delete offers[action.key];
      return {
        ...state,
        [action.marketplaceId]: {
          ...state[action.marketplaceId],
          offers
        }
      };
    case RESET_MARKETPLACE:
      return { ...initialState };
    case UPDATE_BLUEPRINT:
      return {
        ...state,
        blueprints: {
          ...state.blueprints,
          [action.key]: action.data
        }
      };
    case DELETE_BLUEPRINT:
      const newBlueprints = { ...state.blueprints };
      delete newBlueprints[action.key];
      return {
        ...state,
        blueprints: newBlueprints
      };
    default:
      return state;
  }
}

export default marketplaceReducer;

/* Selectors */
export function getIsMarketplace() {
  return isEnabled("marketplace-prod");
}

export function getAllowGlobalPublish() {
  return isEnabled("publish-to-aible-global-marketplace");
}

export function getMarketplace(state) {
  return state.marketplace;
}

export function getIsLoading(state) {
  return getMarketplace(state).isLoading;
}

export function getMarketplaceIds(state) {
  const keys = Object.keys(getMarketplace(state));
  return keys.filter((elem) => !isNaN(parseInt(elem)));
}

export function getMarketplaceArr(state) {
  return getMarketplaceIds(state).map((id) => getMarketplaceById(state, id));
}

export function getMarketplaceById(state, id) {
  return getMarketplace(state)[id] || {};
}

export function getInternalMarketplaces(state) {
  let markets = getMarketplaceArr(state);
  return markets.filter((market) => {
    const flags = (market && market.flags) || "";
    return (
      flags.includes(MARKETPLACE_FLAGS.PUBLISHED_INTERNAL) && !flags.includes(MARKETPLACE_FLAGS.PUBLISHED_EXTERNAL)
    );
  });
}

export function getInternalMarketplace(state) {
  let markets = getMarketplaceIds(state);
  for (let id of markets) {
    const market = getMarketplaceById(state, id);
    const flags = (market && market.flags) || "";
    if (flags.includes(MARKETPLACE_FLAGS.PUBLISHED_INTERNAL) && !flags.includes(MARKETPLACE_FLAGS.PUBLISHED_EXTERNAL)) {
      return id;
    }
  }
  return null;
}

export function getSelected(state) {
  return getMarketplace(state).selected;
}

export function getSelectedMarketplace(state) {
  return getMarketplaceById(state, getSelected(state));
}

export function getMarketplaceOffers(state, id) {
  return getMarketplaceById(state, id).offers || {};
}

export function getMarketplaceOffersArr(state, id) {
  return Object.values(getMarketplaceOffers(state, id));
}

export function getBlueprints(state) {
  return getMarketplace(state).blueprints;
}

export function getBlueprint(state, id) {
  return getBlueprints(state)[id];
}

/* Actions */
export function updateIsLoading(data = false) {
  return { type: UPDATE_FIELD, data, key: "isLoading" };
}

export function updateMarketplace(data) {
  return { type: UPDATE_FIELD, data, key: data.id };
}

export function updateOffer(data) {
  return { type: UPDATE_OFFER, marketplaceId: data.marketplace_id, data, key: data.id };
}

export function deleteOffer(data) {
  return { type: DELETE_OFFER, marketplaceId: data.marketplace_id, key: data.id };
}

export function updateSelected(data) {
  return { type: UPDATE_FIELD, data, key: "selected" };
}

export function updateMarketplaceOffers(marketplaceId, data) {
  return { type: UPDATE_MARKETPLACE, marketplaceId, data, key: "offers" };
}

export function resetMarketplace() {
  return { type: RESET_MARKETPLACE };
}

export function updateBlueprints(blueprints) {
  return { type: UPDATE_FIELD, data: blueprints, key: "blueprints" };
}

export function updateBlueprint(id, blueprint) {
  return { type: UPDATE_BLUEPRINT, data: blueprint, key: id };
}

export function deleteBlueprint(id) {
  return { type: DELETE_BLUEPRINT, key: id };
}

/* Methods */
function createInternalMarketplaceName(state) {
  const selectedTenant = loginReducer.getSelectedTenant(state);
  const marketplaceName = (selectedTenant ? selectedTenant.fullname + " " : "") + "Internal Marketplace";
  return marketplaceName;
}

function hasInternalMarketplace(state) {
  let markets = getMarketplaceIds(state);
  markets = markets.filter((id) => {
    const market = getMarketplaceById(state, id);
    const flags = (market && market.flags && market.flags) || "";
    return (
      flags.includes(MARKETPLACE_FLAGS.PUBLISHED_INTERNAL) && !flags.includes(MARKETPLACE_FLAGS.PUBLISHED_EXTERNAL)
    );
  });
  return markets && markets.length > 0;
}

export function init() {
  return (dispatch, getState) => {
    const state = getState();
    const markets = getMarketplaceIds(state);
    if (!markets || markets.length < 1) {
      return dispatch(fetchMarketplaces()).then((response) => {
        const state = getState();
        const markets = getMarketplaceIds(state);
        dispatch(selectMarketplace(markets[0]));
      });
    } else {
      dispatch(selectMarketplace(markets[0]));
    }
  };
}

export function createInternalMarketplace() {
  return (dispatch, getState) => {
    const state = getState();
    const internalMarket = {
      title: createInternalMarketplaceName(state),
      description: INTERNAL_MARKETPLACE_DESCRIPTION,
      public: false
    };
    return dispatch(createMarketplace(internalMarket)).then((response) => {
      dispatch(selectMarketplace(response.id));
    });
  };
}

export function createMarketplace(marketplace) {
  return (dispatch, getState) => {
    return MarketplaceAPI.createMarketplace(marketplace).then((response) => {
      dispatch(updateMarketplace(response));
      dispatch(fetchOffers(response.id));
    });
  };
}

// Unfinished, NEED TO REMOVE MARKETPLACE FROM STATE
export function deleteMarketplace(marketplaceId) {
  return (dispatch, getState) => {
    const newUpdate = {
      flags: MARKETPLACE_FLAGS.UNPUBLISHED
    };
    return MarketplaceAPI.editMarketplace(marketplaceId, newUpdate);
  };
}

function removeUnpublishedMarketplaces(marketplaces) {
  return marketplaces.filter((market) => {
    const flags = (market && market.flags) || "";
    return !flags.includes(MARKETPLACE_FLAGS.UNPUBLISHED);
  });
}

export function fetchMarketplaces() {
  return (dispatch, getState) => {
    dispatch(updateIsLoading(true));
    return MarketplaceAPI.getMarketplaces().then((response) => {
      let activeMarketplaces = removeUnpublishedMarketplaces(response);
      return Promise.all(
        activeMarketplaces.map((marketplace) => {
          dispatch(updateMarketplace(marketplace));
          return dispatch(fetchOffers(marketplace.id));
        })
      ).then((res) => {
        dispatch(updateIsLoading(false));
        return activeMarketplaces;
      });
    });
  };
}

export function fetchOffers(marketplaceId) {
  return (dispatch, getState) => {
    return MarketplaceAPI.getMarketplaceOffers(marketplaceId).then((response) => {
      const offers = {};
      response.forEach((offer) => {
        offers[offer.id] = offer;
      });
      dispatch(updateMarketplaceOffers(marketplaceId, offers));
      return offers;
    });
  };
}

export function selectMarketplace(marketplaceId) {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(updateSelected(marketplaceId));
    if (getMarketplaceOffersArr(state, marketplaceId).length < 1) {
      return dispatch(fetchOffers(marketplaceId));
    }
  };
}

export function publishBlueprint(blueprintId, marketplaceId) {
  return (dispatch, getState) => {
    const state = getState();
    const name = blueprintReducer.getMarketplaceName(state, blueprintId);
    const description = blueprintReducer.getMarketplaceDescription(state, blueprintId);
    const offer = {
      type: "blueprint",
      name,
      description,
      blueprint_id: parseInt(blueprintId),
      status: OFFER_STATUS.PUBLISHED
    };
    return MarketplaceAPI.publishToMarketplace(marketplaceId, offer).then((response) => {
      dispatch(updateOffer(response));
      toast("Blueprint Published to Internal Marketplace", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
      dispatch(blueprintReducer.resetMarketplaceDetails(blueprintId));
    });
  };
}

export function unpublishBlueprint(offerId) {
  return (dispatch, getState) => {
    return MarketplaceAPI.deleteOffer(offerId).then((response) => {
      dispatch(deleteOffer(response));
      toast("Blueprint Removed from Marketplace", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
    });
  };
}

export function purchaseBlueprint(offerId) {
  return (dispatch, getState) => {
    return MarketplaceAPI.buyBlueprint(offerId).then((response) => {
      // dispatch(updateOffer(response));
      return dispatch(blueprintReducer.fetchNextBlueprints(response.entity_id)).then((bpResponse) => {
        toast("Added to Your Blueprints", {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
      });
    });
  };
}

export function executeSearch(term, targetObj) {
  return Object.values(targetObj).filter((elem) => {
    if (!term) return true;
    if (!elem) return false;
    const parsedTarget = (elem.name || "") + (elem.label || "") + (elem.model_name || "");
    return parsedTarget && parsedTarget.toLowerCase().includes(term.toLowerCase());
  });
}

export function isOfferCreator(offer, userId) {
  if (!offer || !offer.created_by) {
    return false;
  }
  return offer.created_by.id === userId;
}

export function isOfferPurchased(offer) {
  if (!offer) {
    return false;
  }
  // return offer.hasOwnProperty("purchase_id");
  // Temporarily removing purchased banner
  return false;
}

export function sortOffers(type, offers) {
  let filteredOffers = offers;
  if (type && Object.values(OFFER_TYPE).includes(type)) {
    filteredOffers = offers.filter((elem) => elem.type === type);
  }
  const userId = getUserID();
  return filteredOffers.sort((offer1, offer2) => {
    const offer1Val = {
      isCreator: isOfferCreator(offer1, userId),
      isPurchased: isOfferPurchased(offer1, userId),
      name: offer1.name
    };
    const offer2Val = {
      isCreator: isOfferCreator(offer2, userId),
      isPurchased: isOfferPurchased(offer2, userId),
      name: offer2.name
    };
    if (offer1Val.isCreator !== offer2Val.isCreator) {
      return offer1Val.isCreator ? 1 : -1;
    }
    if (offer1Val.isPurchased !== offer2Val.isPurchased) {
      return offer1Val.isPurchased ? 1 : -1;
    }
    return offer1Val.name > offer2Val.name ? 1 : -1;
  });
}

export function parseOffer(offer, userId) {
  const parsedOffer = {
    name: "",
    author: "",
    description: "",
    rating: {
      value: 0,
      numReviews: 0,
      userRated: false
    },
    isPurchased: false,
    isCreator: false
  };
  if (!offer) {
    return parsedOffer;
  }
  parsedOffer.name = offer.name || "";
  parsedOffer.author = (offer.created_by && offer.created_by.fullname) || "";
  parsedOffer.description = offer.description || "";
  parsedOffer.isPurchased = isOfferPurchased(offer);
  parsedOffer.isCreator = isOfferCreator(offer, userId);
  return parsedOffer;
}

export function filterOtherUsers(data = [], state) {
  // Temporary fix for disappearing stuff (list by user coming soon)
  return data;
  if (getIsMarketplace() && (!state || !getIsDefaultAccount(state))) {
    const userID = getUserID(state);
    const filteredData = data.filter((elem) => elem.created_by && elem.created_by.id === userID);
    return filteredData;
  } else {
    return data;
  }
}

export function filterBlueprints(blueprints, state) {
  const userId = getUserID(state);
  const marketplaceBps = {};
  const bpIds = Object.keys(blueprints);
  bpIds.forEach((key) => {
    const blueprint = blueprints[key];
    if (!blueprint) {
      return delete blueprint[key];
    }
    if (blueprint.wizard && blueprint.wizard.isMarketplace) {
      marketplaceBps[key] = blueprint;
      delete blueprints[key];
    } else if (blueprint.created_by && blueprint.created_by.id !== userId) {
      // MarketplaceDemo
      if (
        (blueprint.wizard &&
          blueprint.wizard.marketplace &&
          (blueprint.wizard.marketplace.list || blueprint.wizard.marketplace.purchased)) ||
        !getIsMarketplace() ||
        (state && getIsDefaultAccount(state))
      ) {
        return;
      }

      // delete blueprints[key];
    }
  });
  return {
    blueprints,
    marketplaceBps
  };
}

// export function sortBlueprints(type, targetArr) {
//   return targetArr.sort((bp1, bp2) => {
//     const userID = getUserID();
//     const bp1Val = {
//       isCreator: isBpCreator(bp1, userID),
//       isPurchased: isBpPurchased(bp1, userID),
//       name: bp1.name
//     };
//     const bp2Val = {
//       isCreator: isBpCreator(bp2, userID),
//       isPurchased: isBpPurchased(bp2, userID),
//       name: bp2.name
//     };
//     if (bp1Val.isCreator !== bp2Val.isCreator) {
//       return bp1Val.isCreator ? 1 : -1;
//     }
//     if (bp1Val.isPurchased !== bp2Val.isPurchased) {
//       return bp1Val.isPurchased ? 1 : -1;
//     }
//     return bp1Val.name > bp2Val.name ? 1 : -1;
//   });
// }

// export function isBpCreator(blueprint, userID) {
//   if (!blueprint || !(blueprint && blueprint.wizard) || !blueprint.wizard.marketplace) {
//     return false;
//   }
//   return blueprint.created_by.id === userID;
// }

// export function isBpPurchased(blueprint, userID) {
//   if (!blueprint || !(blueprint && blueprint.wizard) || !blueprint.wizard.marketplace) {
//     return false;
//   }
//   const purchasedBy = blueprint.wizard.marketplace.purchased_by || {};
//   return Boolean(purchasedBy[userID]);
// }

// export function parseItem(item, type) {
//   const parsedItem = {
//     name: "",
//     author: "",
//     description: "",
//     rating: {
//       value: 0,
//       numReviews: 0,
//       userRated: false
//     },
//     isPurchased: false,
//     isCreator: false
//   };
//   const userID = getUserID();
//   if (!item || !(item && item.wizard) || !item.wizard.marketplace) {
//     return parsedItem;
//   }
//   switch (type) {
//     case "blueprint":
//       parsedItem.name = item.name;
//       parsedItem.author = item.created_by.fullname;
//       parsedItem.description = item.wizard.marketplace.description;
//       if (item.wizard.marketplace.rating) {
//         const ratedBy = item.wizard.marketplace.rating.rated_by || {};
//         parsedItem.rating.value = item.wizard.marketplace.rating.value;
//         parsedItem.rating.numReviews = Object.keys(ratedBy).length;
//         parsedItem.userRated = !!ratedBy[userID];
//       }
//       parsedItem.isCreator = isBpCreator(item);
//       if (!parsedItem.isCreator) {
//         parsedItem.isPurchased = isBpPurchased(item);
//       }
//       break;
//     default:
//   }
//   return parsedItem;
// }

export function getOriginalAuthor(id) {
  return (dispatch, getState) => {
    const state = getState();
    const bp = getBlueprint(state, id);
    return bp && bp.created_by ? bp.created_by.fullname : "";
  };
}

// export function publishBlueprint(id) {
//   return (dispatch, getState) => {
//     const state = getState();
//     const name = getNewBlueprintName(state);
//     const description = getNewBlueprintDescrip(state);

//     const marketplaceDetails = {
//       isMarketplace: true,
//       marketplace: {
//         name,
//         description,
//         rating: {
//           value: 0,
//           rated_by: {}
//         },
//         purchased_by: {},
//         created_by: {
//           id: getUserID(),
//           fullname: getUserName()
//         },
//         original_bp: id
//       }
//     };

//     return dispatch(blueprintReducer.createBlueprint(id, marketplaceDetails)).then(response => {
//       dispatch(resetNewBlueprint());
//       return response;
//     });
//   };
// }

// export function unpublishBlueprint(id) {
//   return (dispatch, getState) =>
//     BlueprintAPI.handleDelete(id).then(response => {
//       toast("Blueprint Removed from Marketplace", {
//         type: toast.TYPE.SUCCESS,
//         position: toast.POSITION.TOP_CENTER,
//         autoClose: 7500
//       });
//       return dispatch(deleteBlueprint(id));
//     });
// }

// export function purchaseBlueprint(id) {
//   return (dispatch, getState) => {
//     const state = getState();
//     const userID = getUserID();

//     const blueprint = getBlueprint(state, id);
//     if (
//       blueprint.wizard.marketplace &&
//       blueprint.wizard.marketplace.purchased_by &&
//       blueprint.wizard.marketplace.purchased_by[userID]
//     ) {
//       blueprint.wizard.marketplace.purchased_by[userID] =
//         blueprint.wizard.marketplace.purchased_by[userID] + 1;
//     } else {
//       blueprint.wizard.marketplace.purchased_by[userID] = 1;
//     }
//     const purchasedBPWizard = JSON.parse(JSON.stringify(blueprint.wizard));
//     purchasedBPWizard.isMarketplace = undefined;

//     dispatch(updateIsBlueprintCreating(true));

//     return BlueprintAPI.createBlueprint(purchasedBPWizard).then(createRes => {
//       dispatch(blueprintReducer.updateBlueprint(createRes.id, createRes));

//       return BlueprintAPI.updateBlueprint(id, blueprint.wizard).then(updateRes => {
//         toast("Added to Your Blueprints", {
//           type: toast.TYPE.SUCCESS,
//           position: toast.POSITION.TOP_CENTER,
//           autoClose: 7500
//         });

//         dispatch(updateBlueprint(updateRes.id, updateRes));
//         return dispatch(updateIsBlueprintCreating(false));
//       });
//     });
//   };
// }
